import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import type { BFFOutput } from "@tracksuit/bff/trpc";

// Type alias to reduce verbosity
export type Account = NonNullable<BFFOutput["metadata"]["list"]["accounts"]>[0];

type AccountStore = {
  compact: boolean;
  active?: Account;
  activeAccountBrandId?: number;
  accounts: Account[];
  reset(): void;
  populate(accounts: Account[], compact: boolean): void;
  activateAccountBrand(account: Account): void;
  activate(accountBrandId: number): void;
};


export const useAccount = createWithEqualityFn<AccountStore>()(
  (set, get) => ({
    compact: false,
    active: undefined,
    activeAccountBrandId: undefined,
    accounts: [],

    // Complete reset - clears all state
    reset: () =>
      set({
        accounts: [],
        active: undefined,
        activeAccountBrandId: undefined,
      }),

    populate: (accounts, compact) => set({ accounts, compact }),

    // Sets both active and activeAccountBrandId to keep them in sync
    activateAccountBrand: (account) => {
      set({
        active: account,
        activeAccountBrandId: account.accountBrandId,
      });
    },

    // Improved logic with validation in both modes
    activate: (accountBrandId) => {
      const { accounts, compact } = get();

      // Always validate
      if (!accounts.length) {
        throw new Error("No accounts available");
      }

      const account = accounts.find(
        (acc) => acc.accountBrandId === accountBrandId,
      );

      if (!account) {
        throw new Error(`Account with ID ${accountBrandId} not found`);
      }

      // In non-compact mode, set both (we have full data)
      if (!compact) {
        set({
          active: account,
          activeAccountBrandId: accountBrandId,
        });
      } else {
        // In compact mode, just set ID (active comes later from detail API)
        set({ activeAccountBrandId: accountBrandId });
      }
    },
  }),
  shallow,
);

